import { ReactNode, useContext } from 'react';

import { Box, SxProps, ThemeProvider, Typography } from '@mui/material';
import { StudentSectionSettingTypeCodes } from 'const';
import { PortfolioSettingsContext } from 'context';
import { useAppSelector } from 'hooks';
import { profileTheme } from 'portfolio3/styles/theme';
import { Button } from 'portfolio3/ui-kit/button';
import Toggle from 'portfolio3/ui-kit/Toggle';
import {
  configSelector,
  linkVisibilitySettingsSelector,
  localVisibilitySettingsSelector,
  visibilitySettingsSelector,
} from 'selectors';
import { IViewVisibilitySetting } from 'types';
import { isDefined } from 'utils';
import { findTargetVisibilitySetting } from 'utils/localVisibilitySettingsService';

interface IUseWidgetVisibilitySettingsProps {
  sectionTypeCode: string;
  subsectionTypeCode: string;
  subcategoryTypeCode?: string;
}

interface IUseWidgetVisibilityResult {
  isSettingsMode: boolean;
  isWidgetVisible: boolean;
  toggleElement?: ReactNode;
  overlayElement?: ReactNode;
}

const toggleStyle: SxProps = {
  zIndex: 11,
};

const overlayStyle: SxProps = {
  zIndex: 10,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',

  background: 'rgba(255, 255, 255, 0.8)',
};

const titleStyle: SxProps = {
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '36px',
};

const showButtonStyle: SxProps = {
  marginTop: '16px',
};

const useWidgetVisibility = ({
  sectionTypeCode,
  subsectionTypeCode,
  subcategoryTypeCode,
}: IUseWidgetVisibilitySettingsProps): IUseWidgetVisibilityResult => {
  const config = useAppSelector(configSelector);
  const hideProfessionEducation = config?.HIDE_PROFESSION_EDUCATION === true;
  const hideProforientation = config?.HIDE_PROFESSION_PROFORIENTATION === true;
  const hideApplications = config?.HIDE_PROFESSION_APPLICATIONS === true;
  const hideRecommendations = config?.HIDE_PROFESSION_RECOMMENDATIONS === true;
  const hideEvents = config?.HIDE_PROFESSION_EVENTS === true;
  const hideGia = config?.HIDE_STUDY_GIA === true;
  const hideDiagnostics = config?.HIDE_STUDY_DIAGNOSTICS === true;
  const hideTrialExam = config?.HIDE_STUDY_TRIAL_EXAM === true;
  const hideTraining = config?.HIDE_STUDY_TRAINING === true;
  const hidePractice = config?.HIDE_STUDY_PRACTICE === true;
  const hideDocuments = config?.HIDE_STUDY_DOCUMENTS === true;
  const hideProfileRecommendations = config?.HIDE_PROFILE_RECOMMENDATIONS === true;
  const hideCulture = config?.HIDE_CULTURE === true;

  const linkMode = useAppSelector((state) => state.linkMode.mode);
  const visibilitySettings = useAppSelector(visibilitySettingsSelector);
  const linkVisibilitySettings = useAppSelector(linkVisibilitySettingsSelector);
  const localVisibilitySettings = useAppSelector(localVisibilitySettingsSelector);

  const { isSettingsMode, setSubsectionVisibility, setSubcategoryVisibility } = useContext(PortfolioSettingsContext);

  const typeCodes = [sectionTypeCode, subsectionTypeCode, subcategoryTypeCode].filter(isDefined);

  // Не отображаем виджеты до загрузки конфига
  if (!config) {
    return {
      isSettingsMode: false,
      isWidgetVisible: false,
    };
  }

  const filterSubSections = (setting: IViewVisibilitySetting) => {
    if (hideProfessionEducation && setting.sectionTypeCode === StudentSectionSettingTypeCodes.professionEducation) {
      return false;
    }

    if (hideProforientation && setting.sectionTypeCode === StudentSectionSettingTypeCodes.professionProforientation) {
      return false;
    }

    if (hideApplications && setting.sectionTypeCode === StudentSectionSettingTypeCodes.professionApplications) {
      return false;
    }

    if (
      hideRecommendations &&
      setting.sectionTypeCode === StudentSectionSettingTypeCodes.professionProforientationRecommendations
    ) {
      return false;
    }

    if (hideEvents && setting.sectionTypeCode === StudentSectionSettingTypeCodes.professionProforientationEvents) {
      return false;
    }

    if (hideGia && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyGia) {
      return false;
    }

    if (hideDiagnostics && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyDiagnostic) {
      return false;
    }

    if (hideTrialExam && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyTrialExam) {
      return false;
    }

    if (hideTraining && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyTraining) {
      return false;
    }

    if (hidePractice && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyPractice) {
      return false;
    }

    if (hideDocuments && setting.sectionTypeCode === StudentSectionSettingTypeCodes.studyDocuments) {
      return false;
    }

    if (
      hideProfileRecommendations &&
      setting.sectionTypeCode === StudentSectionSettingTypeCodes.profileRecommendations
    ) {
      return false;
    }

    return true;
  };

  const mapSettings = (setting: IViewVisibilitySetting) => {
    if (
      setting.sectionTypeCode === StudentSectionSettingTypeCodes.profession ||
      setting.sectionTypeCode === StudentSectionSettingTypeCodes.study
    ) {
      return {
        ...setting,
        subSections: setting.subSections?.filter(filterSubSections),
      };
    }
    return setting;
  };

  /* режим настройки */
  if (isSettingsMode) {
    const hasSubcategory = Boolean(subcategoryTypeCode);
    const localSettingsWithFilteredSubsections = localVisibilitySettings.map(mapSettings);
    const localSetting = findTargetVisibilitySetting(localSettingsWithFilteredSubsections, typeCodes);
    const isAdminEnabled = localSetting?.isAdminVisible;
    const isLocalVisible = localSetting?.computedVisibility ?? false;

    const handleSetVisibility = (isVisible: boolean) => {
      return () => {
        if (!sectionTypeCode || !subsectionTypeCode) return;

        if (subcategoryTypeCode) {
          setSubcategoryVisibility(
            localSettingsWithFilteredSubsections,
            sectionTypeCode,
            subsectionTypeCode,
            subcategoryTypeCode,
            isVisible,
          );
        } else {
          setSubsectionVisibility(localSettingsWithFilteredSubsections, sectionTypeCode, subsectionTypeCode, isVisible);
        }
      };
    };

    const overlayTitle = hasSubcategory ? 'Подраздел скрыт' : 'Раздел скрыт';

    const toggleElement = (
      <Toggle
        checked={isLocalVisible}
        onChange={handleSetVisibility(!isLocalVisible)}
        sx={toggleStyle}
        size={hasSubcategory ? 'small' : 'medium'}
      />
    );

    const overlayElement = !isLocalVisible && (
      <Box className="visibility-overlay" sx={overlayStyle}>
        <Typography sx={titleStyle}>{overlayTitle}</Typography>
        <ThemeProvider theme={profileTheme}>
          <Button variant="outline" onClick={handleSetVisibility(true)} sx={showButtonStyle}>
            Показать
          </Button>
        </ThemeProvider>
      </Box>
    );

    return {
      isSettingsMode: true,
      isWidgetVisible: isAdminEnabled ?? false,
      toggleElement,
      overlayElement,
    };
  }

  /* режим просмотра портфолио */
  const settings = linkMode ? linkVisibilitySettings : visibilitySettings;
  const filteredSettings = settings.filter((setting) => {
    if (hideCulture && setting.sectionTypeCode === StudentSectionSettingTypeCodes.culture) {
      return false;
    }

    return true;
  });
  const settingsWithFilteredSubSections = filteredSettings.map(mapSettings);
  const sectionSettings = findTargetVisibilitySetting(settingsWithFilteredSubSections, typeCodes);
  const isVisible = sectionSettings?.computedVisibility ?? false;

  return {
    isSettingsMode: false,
    isWidgetVisible: isVisible,
  };
};

export default useWidgetVisibility;
